import React, { useState, useEffect, useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  AcademicCapIcon,
  ClockIcon,
  CalendarIcon,
  DocumentTextIcon,
  PlusIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  StarIcon,
  CheckCircleIcon,
  XCircleIcon,
  ChartBarIcon,
  BookOpenIcon,
  CpuChipIcon,
  LightBulbIcon,
  XMarkIcon,
  SparklesIcon
} from '@heroicons/react/24/outline'
import { StudentPageLayout } from '../../components/student/StudentPageLayout'
import { StudentSection } from '../../components/student/StudentSection'
import toast from 'react-hot-toast'
import api from '../../utils/api'

// 考试数据接口
interface Exam {
  id: string
  title: string
  subject: string
  course?: {
    id: string
    title: string
    teacher: string
  }
  difficulty: 'easy' | 'medium' | 'hard'
  duration: number // 分钟
  totalQuestions: number
  type: 'practice' | 'quiz' | 'midterm' | 'final' | 'custom'
  status: 'available' | 'in_progress' | 'completed' | 'expired'
  dueDate?: Date
  description: string
  tags: string[]
  aiGenerated: boolean
  attempts: number
  maxAttempts: number
  bestScore?: number
  lastAttemptDate?: Date
  createdAt: Date
}

const ExamCenter: React.FC = () => {
  const navigate = useNavigate()
  const [exams, setExams] = useState<Exam[]>([])
  const [filteredExams, setFilteredExams] = useState<Exam[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [filterStatus, setFilterStatus] = useState<string>('all')
  const [filterType, setFilterType] = useState<string>('all')
  const [filterDifficulty, setFilterDifficulty] = useState<string>('all')
  const [isLoading, setIsLoading] = useState(true)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showAIModal, setShowAIModal] = useState(false)
  const [isGenerating, setIsGenerating] = useState(false)
  const [aiFormData, setAIFormData] = useState({
    title: '',
    subject: '',
    grade: '',
    totalQuestions: 10,
    duration: 60,
    knowledgePoints: '',
    difficultyDistribution: { easy: 30, medium: 50, hard: 20 }
  })

  // 加载考试数据
  useEffect(() => {
    loadExams()
  }, [])

  // 筛选考试
  useEffect(() => {
    let filtered = exams

    // 搜索过滤
    if (searchTerm) {
      filtered = filtered.filter(exam =>
        exam.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        exam.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
        exam.description.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    // 状态过滤
    if (filterStatus !== 'all') {
      filtered = filtered.filter(exam => exam.status === filterStatus)
    }

    // 类型过滤
    if (filterType !== 'all') {
      filtered = filtered.filter(exam => exam.type === filterType)
    }

    // 难度过滤
    if (filterDifficulty !== 'all') {
      filtered = filtered.filter(exam => exam.difficulty === filterDifficulty)
    }

    setFilteredExams(filtered)
  }, [exams, searchTerm, filterStatus, filterType, filterDifficulty])

  const loadExams = async () => {
    setIsLoading(true)
    
    try {
      const token = localStorage.getItem('token')
      if (!token) {
        setExams([])
        return
      }

      // 调用真实的考试 API
      const [examsRes, sessionsRes] = await Promise.allSettled([
        fetch('/api/exams', {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
        }),
        fetch('/api/students/exams', {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
        })
      ])

      let examList: Exam[] = []

      // 获取考试列表
      if (examsRes.status === 'fulfilled' && examsRes.value.ok) {
        const examsData = await examsRes.value.json()
        // 确保 rawExams 是数组
        let rawExams = examsData.exams || examsData.data || examsData || []
        if (!Array.isArray(rawExams)) {
          console.warn('考试数据不是数组格式:', rawExams)
          rawExams = []
        }
        
        examList = rawExams.map((exam: any) => ({
          id: exam.id,
          title: exam.title || '未命名考试',
          subject: exam.subject || '综合',
          course: exam.course ? {
            id: exam.course.id,
            title: exam.course.title || exam.course.subject,
            teacher: exam.course.teacher?.user 
              ? `${exam.course.teacher.user.firstName || ''}${exam.course.teacher.user.lastName || '老师'}`
              : '老师'
          } : undefined,
          difficulty: exam.difficulty || 'medium',
          duration: exam.duration || 60,
          totalQuestions: exam.totalQuestions || exam.questions?.length || 0,
          type: exam.type || 'practice',
          status: exam.status === 'published' ? 'available' : exam.status || 'available',
          dueDate: exam.endTime ? new Date(exam.endTime) : undefined,
          description: exam.description || '',
          tags: exam.tags || [],
          aiGenerated: exam.aiGenerated || false,
          attempts: 0,
          maxAttempts: exam.maxAttempts || 3,
          createdAt: new Date(exam.createdAt || Date.now())
        }))
      }

      // 获取学生的考试会话数据来更新尝试次数和成绩
      if (sessionsRes.status === 'fulfilled' && sessionsRes.value.ok) {
        const sessionsData = await sessionsRes.value.json()
        // 确保 sessions 是数组
        let sessions = sessionsData.sessions || sessionsData.data || []
        if (!Array.isArray(sessions)) {
          console.warn('会话数据不是数组格式:', sessions)
          sessions = []
        }
        
        // 按考试ID分组会话
        const sessionsByExam = sessions.reduce((acc: Record<string, any[]>, session: any) => {
          const examId = session.examId
          if (!acc[examId]) acc[examId] = []
          acc[examId].push(session)
          return acc
        }, {})

        // 更新考试列表的尝试次数和最高分
        examList = examList.map(exam => {
          const examSessions = sessionsByExam[exam.id] || []
          const completedSessions = examSessions.filter((s: any) => 
            s.status === 'completed' || s.status === 'submitted'
          )
          const scores = completedSessions
            .filter((s: any) => s.latestResult?.score !== undefined)
            .map((s: any) => s.latestResult?.score || 0)
          
          const bestScore = scores.length > 0 ? Math.max(...scores) : undefined
          const hasInProgress = examSessions.some((s: any) => s.status === 'in_progress')
          
          return {
            ...exam,
            attempts: examSessions.length,
            bestScore,
            lastAttemptDate: examSessions.length > 0 
              ? new Date(examSessions[0].updatedAt || examSessions[0].createdAt)
              : undefined,
            status: hasInProgress ? 'in_progress' : 
                   completedSessions.length > 0 && exam.attempts >= exam.maxAttempts ? 'completed' : 
                   exam.status
          }
        })
      }

      setExams(examList)
    } catch (error) {
      console.error('加载考试数据失败:', error)
      setExams([])
    } finally {
      setIsLoading(false)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'available': return 'text-green-600 bg-green-50'
      case 'in_progress': return 'text-blue-600 bg-blue-50'
      case 'completed': return 'text-gray-600 bg-gray-50'
      case 'expired': return 'text-red-600 bg-red-50'
      default: return 'text-gray-600 bg-gray-50'
    }
  }

  const getStatusText = (status: string) => {
    switch (status) {
      case 'available': return '可参加'
      case 'in_progress': return '进行中'
      case 'completed': return '已完成'
      case 'expired': return '已过期'
      default: return '未知'
    }
  }

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'easy': return 'text-green-600 bg-green-50'
      case 'medium': return 'text-yellow-600 bg-yellow-50'
      case 'hard': return 'text-red-600 bg-red-50'
      default: return 'text-gray-600 bg-gray-50'
    }
  }

  const getDifficultyText = (difficulty: string) => {
    switch (difficulty) {
      case 'easy': return '简单'
      case 'medium': return '中等'
      case 'hard': return '困难'
      default: return '未知'
    }
  }

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'practice': return <BookOpenIcon className="w-5 h-5" />
      case 'quiz': return <DocumentTextIcon className="w-5 h-5" />
      case 'midterm': return <AcademicCapIcon className="w-5 h-5" />
      case 'final': return <StarIcon className="w-5 h-5" />
      case 'custom': return <LightBulbIcon className="w-5 h-5" />
      default: return <DocumentTextIcon className="w-5 h-5" />
    }
  }

  const getTypeText = (type: string) => {
    switch (type) {
      case 'practice': return '练习'
      case 'quiz': return '小测'
      case 'midterm': return '期中'
      case 'final': return '期末'
      case 'custom': return '自定义'
      default: return '测试'
    }
  }

  const generateAIExam = () => {
    setShowAIModal(true)
  }

  const handleAIGenerate = async () => {
    if (!aiFormData.title.trim()) {
      toast.error('请输入考试标题')
      return
    }
    if (!aiFormData.subject.trim()) {
      toast.error('请选择科目')
      return
    }

    setIsGenerating(true)
    try {
      const response = await api.post('/exams/ai-generate', {
        title: aiFormData.title,
        subject: aiFormData.subject,
        grade: aiFormData.grade,
        totalQuestions: aiFormData.totalQuestions,
        duration: aiFormData.duration,
        knowledgePoints: aiFormData.knowledgePoints.split(',').map(s => s.trim()).filter(Boolean),
        difficultyDistribution: aiFormData.difficultyDistribution,
        typeDistribution: { single_choice: 60, multiple_choice: 20, true_false: 10, short_answer: 10 }
      })

      if (response.data.success) {
        toast.success(`AI 考试创建成功！共 ${response.data.data.questionCount} 道题目，正在跳转...`)
        setShowAIModal(false)
        // 重置表单
        setAIFormData({
          title: '',
          subject: '',
          grade: '',
          totalQuestions: 10,
          duration: 60,
          knowledgePoints: '',
          difficultyDistribution: { easy: 30, medium: 50, hard: 20 }
        })
        // 自动跳转到答题界面
        setTimeout(() => {
          navigate(`/student/exams/${response.data.data.id}/taking`)
        }, 500)
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'AI 生成失败，请稍后重试')
    } finally {
      setIsGenerating(false)
    }
  }

  const fetchExams = async () => {
    try {
      const response = await api.get('/exams')
      if (response.data.success) {
        setExams(response.data.data.map((exam: any) => ({
          id: exam.id,
          title: exam.title,
          subject: exam.subject || '综合',
          difficulty: exam.difficulty || 'medium',
          duration: exam.duration,
          totalQuestions: exam.questionCount || 0,
          type: exam.type || 'practice',
          status: exam.status === 'published' ? 'available' : exam.status,
          description: exam.description || '',
          tags: exam.tags || [],
          aiGenerated: exam.aiGenerated || false,
          attempts: exam.attempts || 0,
          maxAttempts: exam.maxAttempts || 1,
          bestScore: exam.bestScore,
          createdAt: new Date(exam.createdAt)
        })))
      }
    } catch (error) {
      console.error('获取考试列表失败:', error)
    }
  }

  const stats = useMemo(() => {
    const available = exams.filter(e => e.status === 'available').length
    const completed = exams.filter(e => e.status === 'completed').length
    const graded = exams.filter(e => typeof e.bestScore === 'number')
    const avgScore =
      graded.reduce((acc, e) => acc + (e.bestScore || 0), 0) /
      Math.max(graded.length, 1)
    const aiGenerated = exams.filter(e => e.aiGenerated).length

    return {
      total: exams.length,
      available,
      completed,
      avgScore: Math.round(avgScore),
      aiGenerated
    }
  }, [exams])

  const metrics = useMemo(() => [
    {
      label: '全部考试',
      value: stats.total || '—',
      helper: '练习 + 正式'
    },
    {
      label: '可参加',
      value: stats.available || '0',
      helper: '当前开放'
    },
    {
      label: '已完成',
      value: stats.completed || '0',
      helper: '历史记录'
    },
    {
      label: 'AI 出题',
      value: stats.aiGenerated || '0',
      helper: '智能生成'
    }
  ], [stats])

  const heroExtra = (
    <div className="space-y-3 sm:space-y-4 text-white">
      <p className="text-xs sm:text-sm text-white/80">
        AI 会根据你的考试习惯生成临考提醒与复习清单，成绩提升轨迹将同步到仪表板。
      </p>
      <div className="grid grid-cols-2 gap-2 sm:gap-4">
        <div className="bg-white/10 rounded-xl sm:rounded-2xl border border-white/20 p-3 sm:p-4">
          <p className="text-[10px] sm:text-xs uppercase tracking-[0.2em] sm:tracking-[0.3em] text-white/70">平均分</p>
          <p className="text-xl sm:text-2xl font-semibold mt-0.5 sm:mt-1">{stats.avgScore || 0}</p>
          <p className="text-[10px] sm:text-xs text-white/70 mt-0.5 sm:mt-1">已批阅考试</p>
        </div>
        <div className="bg-white/10 rounded-xl sm:rounded-2xl border border-white/20 p-3 sm:p-4">
          <p className="text-[10px] sm:text-xs uppercase tracking-[0.2em] sm:tracking-[0.3em] text-white/70">待完成</p>
          <p className="text-xl sm:text-2xl font-semibold mt-0.5 sm:mt-1">{stats.available}</p>
          <p className="text-[10px] sm:text-xs text-white/70 mt-0.5 sm:mt-1">建议本周完成</p>
        </div>
      </div>
    </div>
  )

  return (
    <StudentPageLayout
      title="考试中心"
      description="智能在线考试系统，支持 AI 出题、自动评分与深度分析。"
      metrics={metrics}
      heroExtra={heroExtra}
      actions={
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full sm:w-auto">
          <button
            onClick={generateAIExam}
            className="flex items-center justify-center px-4 py-2.5 sm:py-2 rounded-xl sm:rounded-full bg-gradient-to-r from-purple-600 to-blue-600 text-white text-sm font-medium shadow-lg shadow-purple-500/30 hover:from-purple-700 hover:to-blue-700 transition"
          >
            <CpuChipIcon className="w-4 h-4 mr-2" />
            AI 智能出题
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center justify-center px-4 py-2.5 sm:py-2 rounded-xl sm:rounded-full border border-white/40 text-white text-sm font-medium hover:bg-white/10 transition"
          >
            <PlusIcon className="w-4 h-4 mr-2" />
            创建考试
          </button>
        </div>
      }
    >
      <StudentSection
        title="考试统计"
        description="掌握近期考试表现与待办"
        icon={ChartBarIcon}
      >
        {/* 移动端横向滚动，桌面端网格布局 */}
        <div className="overflow-x-auto -mx-4 px-4 pb-2 md:overflow-visible md:mx-0 md:px-0 md:pb-0">
          <div className="flex gap-3 min-w-max md:min-w-0 md:grid md:grid-cols-2 lg:grid-cols-4 md:gap-4">
            <div className="bg-white rounded-2xl p-4 sm:p-6 border border-gray-100 flex items-center min-w-[160px] md:min-w-0 flex-shrink-0 md:flex-shrink">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
                <DocumentTextIcon className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
              </div>
              <div className="ml-3 sm:ml-4">
                <p className="text-xs sm:text-sm text-gray-500">总考试数</p>
                <p className="text-xl sm:text-2xl font-semibold text-gray-900">{stats.total}</p>
              </div>
            </div>
            <div className="bg-white rounded-2xl p-4 sm:p-6 border border-gray-100 flex items-center min-w-[160px] md:min-w-0 flex-shrink-0 md:flex-shrink">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-green-50 flex items-center justify-center flex-shrink-0">
                <ClockIcon className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
              </div>
              <div className="ml-3 sm:ml-4">
                <p className="text-xs sm:text-sm text-gray-500">可参加</p>
                <p className="text-xl sm:text-2xl font-semibold text-gray-900">{stats.available}</p>
              </div>
            </div>
            <div className="bg-white rounded-2xl p-4 sm:p-6 border border-gray-100 flex items-center min-w-[160px] md:min-w-0 flex-shrink-0 md:flex-shrink">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-purple-50 flex items-center justify-center flex-shrink-0">
                <CheckCircleIcon className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" />
              </div>
              <div className="ml-3 sm:ml-4">
                <p className="text-xs sm:text-sm text-gray-500">已完成</p>
                <p className="text-xl sm:text-2xl font-semibold text-gray-900">{stats.completed}</p>
              </div>
            </div>
            <div className="bg-white rounded-2xl p-4 sm:p-6 border border-gray-100 flex items-center min-w-[160px] md:min-w-0 flex-shrink-0 md:flex-shrink">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-yellow-50 flex items-center justify-center flex-shrink-0">
                <ChartBarIcon className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-600" />
              </div>
              <div className="ml-3 sm:ml-4">
                <p className="text-xs sm:text-sm text-gray-500">平均分数</p>
                <p className="text-xl sm:text-2xl font-semibold text-gray-900">{stats.avgScore}</p>
              </div>
            </div>
          </div>
        </div>
      </StudentSection>
      <StudentSection
        title="搜索与筛选"
        description="根据状态、类型和难度快速定位考试"
        icon={FunnelIcon}
      >
        <div className="space-y-4">
          {/* 搜索框 - 全宽 */}
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="搜索考试..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 sm:py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
            />
          </div>
          {/* 筛选器 - 移动端横向滚动，桌面端网格 */}
          <div className="overflow-x-auto -mx-4 px-4 pb-2 sm:overflow-visible sm:mx-0 sm:px-0 sm:pb-0">
            <div className="flex gap-2 sm:gap-3 min-w-max sm:min-w-0 sm:grid sm:grid-cols-3">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-3 py-2.5 sm:py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white min-w-[120px] sm:min-w-0"
              >
                <option value="all">全部状态</option>
                <option value="available">可参加</option>
                <option value="in_progress">进行中</option>
                <option value="completed">已完成</option>
                <option value="expired">已过期</option>
              </select>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="px-3 py-2.5 sm:py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white min-w-[120px] sm:min-w-0"
              >
                <option value="all">全部类型</option>
                <option value="practice">练习</option>
                <option value="quiz">小测</option>
                <option value="midterm">期中</option>
                <option value="final">期末</option>
                <option value="custom">自定义</option>
              </select>
              <select
                value={filterDifficulty}
                onChange={(e) => setFilterDifficulty(e.target.value)}
                className="px-3 py-2.5 sm:py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white min-w-[120px] sm:min-w-0"
              >
                <option value="all">全部难度</option>
                <option value="easy">简单</option>
                <option value="medium">中等</option>
                <option value="hard">困难</option>
              </select>
            </div>
          </div>
        </div>
      </StudentSection>
      {/* 考试列表 */}
      <div className="space-y-4">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-3 text-gray-600">加载考试列表...</span>
          </div>
        ) : filteredExams.length === 0 ? (
          <div className="text-center py-12">
            <DocumentTextIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">暂无考试</h3>
            <p className="text-gray-500">没有找到符合条件的考试</p>
          </div>
        ) : (
          filteredExams.map((exam) => (
            <motion.div
              key={exam.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
            >
              <div className="p-4 sm:p-6">
                {/* 移动端：垂直堆叠布局 */}
                <div className="flex flex-col gap-4">
                  {/* 顶部：标题和状态 */}
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-2 flex-wrap flex-1 min-w-0">
                      <span className="flex-shrink-0">{getTypeIcon(exam.type)}</span>
                      <h3 className="text-base sm:text-lg font-semibold text-gray-900 line-clamp-1">{exam.title}</h3>
                      {exam.aiGenerated && (
                        <span className="flex-shrink-0 px-2 py-0.5 text-xs font-medium text-purple-600 bg-purple-50 rounded-full">
                          AI
                        </span>
                      )}
                    </div>
                    <span className={`flex-shrink-0 px-2.5 py-1 rounded-full text-xs sm:text-sm font-medium ${getStatusColor(exam.status)}`}>
                      {getStatusText(exam.status)}
                    </span>
                  </div>

                  {/* 描述 */}
                  <p className="text-sm text-gray-600 line-clamp-2">{exam.description}</p>
                  
                  {/* 课程信息 */}
                  {exam.course && (
                    <div className="flex items-center text-sm text-gray-500">
                      <BookOpenIcon className="w-4 h-4 mr-1.5 flex-shrink-0" />
                      <span className="truncate">{exam.course.title} · {exam.course.teacher}</span>
                    </div>
                  )}

                  {/* 标签 */}
                  <div className="flex flex-wrap gap-1.5">
                    {exam.tags.slice(0, 4).map((tag, index) => (
                      <span
                        key={index}
                        className="px-2 py-0.5 text-xs font-medium text-blue-600 bg-blue-50 rounded-full"
                      >
                        {tag}
                      </span>
                    ))}
                    {exam.tags.length > 4 && (
                      <span className="px-2 py-0.5 text-xs text-gray-400">+{exam.tags.length - 4}</span>
                    )}
                  </div>

                  {/* 考试信息 - 移动端网格，桌面端行内 */}
                  <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 sm:gap-4 text-xs sm:text-sm text-gray-500">
                    <div className="flex items-center">
                      <ClockIcon className="w-4 h-4 mr-1 flex-shrink-0" />
                      <span>{exam.duration}分钟</span>
                    </div>
                    <div className="flex items-center">
                      <DocumentTextIcon className="w-4 h-4 mr-1 flex-shrink-0" />
                      <span>{exam.totalQuestions}题</span>
                    </div>
                    <div className="flex items-center">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getDifficultyColor(exam.difficulty)}`}>
                        {getDifficultyText(exam.difficulty)}
                      </span>
                    </div>
                    {exam.dueDate && (
                      <div className="flex items-center">
                        <CalendarIcon className="w-4 h-4 mr-1 flex-shrink-0" />
                        <span className="truncate">截止 {exam.dueDate.toLocaleDateString()}</span>
                      </div>
                    )}
                  </div>

                  {/* 成绩和尝试次数 */}
                  <div className="flex items-center justify-between py-2 border-t border-gray-100">
                    <div className="flex items-center gap-4">
                      {exam.bestScore !== undefined && (
                        <div className="flex items-center gap-1">
                          <span className="text-lg font-bold text-green-600">{exam.bestScore}</span>
                          <span className="text-xs text-gray-500">分(最高)</span>
                        </div>
                      )}
                      <div className="text-xs sm:text-sm text-gray-500">
                        {exam.attempts}/{exam.maxAttempts} 次尝试
                      </div>
                    </div>
                  </div>

                  {/* 操作按钮 - 移动端全宽堆叠，桌面端行内 */}
                  <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                    <Link
                      to={`/student/exams/${exam.id}`}
                      className="flex-1 sm:flex-initial px-4 py-2.5 sm:py-2 text-blue-600 border border-blue-600 rounded-xl hover:bg-blue-50 transition-colors text-sm font-medium text-center"
                    >
                      查看详情
                    </Link>
                    
                    {exam.status === 'available' && exam.attempts < exam.maxAttempts && (
                      <Link
                        to={`/student/exams/${exam.id}/take`}
                        className="flex-1 sm:flex-initial px-4 py-2.5 sm:py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors text-sm font-medium text-center"
                      >
                        开始考试
                      </Link>
                    )}
                    
                    {exam.status === 'in_progress' && (
                      <Link
                        to={`/student/exams/${exam.id}/take`}
                        className="flex-1 sm:flex-initial px-4 py-2.5 sm:py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors text-sm font-medium text-center"
                      >
                        继续考试
                      </Link>
                    )}
                    
                    {exam.status === 'completed' && (
                      <Link
                        to={`/student/exams/${exam.id}/result`}
                        className="flex-1 sm:flex-initial px-4 py-2.5 sm:py-2 bg-gray-600 text-white rounded-xl hover:bg-gray-700 transition-colors text-sm font-medium text-center"
                      >
                        查看成绩
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          ))
        )}
      </div>

      {/* AI 智能出题模态框 */}
      {showAIModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className="bg-white rounded-t-3xl sm:rounded-2xl w-full sm:max-w-lg max-h-[90vh] overflow-y-auto animate-in slide-in-from-bottom-4 sm:zoom-in-95 duration-200">
            <div className="sticky top-0 bg-white p-6 pb-4 border-b border-gray-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-purple-600 to-blue-600 flex items-center justify-center">
                  <SparklesIcon className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">AI 智能出题</h3>
                  <p className="text-xs text-gray-500">智能生成个性化考试题目</p>
                </div>
              </div>
              <button
                onClick={() => setShowAIModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition"
              >
                <XMarkIcon className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">考试标题 *</label>
                <input
                  type="text"
                  value={aiFormData.title}
                  onChange={(e) => setAIFormData({ ...aiFormData, title: e.target.value })}
                  placeholder="例如：期中数学测验"
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">科目 *</label>
                  <select
                    value={aiFormData.subject}
                    onChange={(e) => setAIFormData({ ...aiFormData, subject: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition"
                  >
                    <option value="">选择科目</option>
                    <option value="数学">数学</option>
                    <option value="语文">语文</option>
                    <option value="英语">英语</option>
                    <option value="物理">物理</option>
                    <option value="化学">化学</option>
                    <option value="生物">生物</option>
                    <option value="历史">历史</option>
                    <option value="地理">地理</option>
                    <option value="政治">政治</option>
                    <option value="编程">编程</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">年级</label>
                  <select
                    value={aiFormData.grade}
                    onChange={(e) => setAIFormData({ ...aiFormData, grade: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition"
                  >
                    <option value="">选择年级</option>
                    <option value="小学">小学</option>
                    <option value="初一">初一</option>
                    <option value="初二">初二</option>
                    <option value="初三">初三</option>
                    <option value="高一">高一</option>
                    <option value="高二">高二</option>
                    <option value="高三">高三</option>
                    <option value="大学">大学</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">题目数量</label>
                  <input
                    type="number"
                    min={5}
                    max={50}
                    value={aiFormData.totalQuestions}
                    onChange={(e) => setAIFormData({ ...aiFormData, totalQuestions: parseInt(e.target.value) || 10 })}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">考试时长（分钟）</label>
                  <input
                    type="number"
                    min={10}
                    max={180}
                    value={aiFormData.duration}
                    onChange={(e) => setAIFormData({ ...aiFormData, duration: parseInt(e.target.value) || 60 })}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">知识点（可选，用逗号分隔）</label>
                <textarea
                  value={aiFormData.knowledgePoints}
                  onChange={(e) => setAIFormData({ ...aiFormData, knowledgePoints: e.target.value })}
                  placeholder="例如：二次函数, 一元二次方程, 函数图像"
                  rows={2}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition resize-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">难度分布</label>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">简单</span>
                    <div className="flex items-center gap-2">
                      <input
                        type="range"
                        min={0}
                        max={100}
                        value={aiFormData.difficultyDistribution.easy}
                        onChange={(e) => {
                          const easy = parseInt(e.target.value)
                          const remaining = 100 - easy
                          const medium = Math.round(remaining * 0.7)
                          const hard = remaining - medium
                          setAIFormData({
                            ...aiFormData,
                            difficultyDistribution: { easy, medium, hard }
                          })
                        }}
                        className="w-32"
                      />
                      <span className="text-sm text-gray-900 w-10 text-right">{aiFormData.difficultyDistribution.easy}%</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">中等</span>
                    <span className="text-sm text-gray-900">{aiFormData.difficultyDistribution.medium}%</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">困难</span>
                    <span className="text-sm text-gray-900">{aiFormData.difficultyDistribution.hard}%</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="sticky bottom-0 bg-white p-6 pt-4 border-t border-gray-100 flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => setShowAIModal(false)}
                className="flex-1 px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition"
              >
                取消
              </button>
              <button
                onClick={handleAIGenerate}
                disabled={isGenerating}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl font-medium hover:from-purple-700 hover:to-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isGenerating ? (
                  <>
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    AI 生成中...
                  </>
                ) : (
                  <>
                    <SparklesIcon className="w-5 h-5" />
                    开始生成
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 创建考试模态框 */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className="bg-white rounded-t-3xl sm:rounded-2xl p-6 w-full sm:max-w-md animate-in slide-in-from-bottom-4 sm:zoom-in-95 duration-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">创建考试</h3>
            <p className="text-gray-600 mb-2 text-sm sm:text-base">
              您可以通过以下方式创建考试：
            </p>
            <div className="space-y-3 mb-6">
              <button
                onClick={() => {
                  setShowCreateModal(false)
                  setShowAIModal(true)
                }}
                className="w-full p-4 border border-purple-200 bg-purple-50 rounded-xl text-left hover:bg-purple-100 transition"
              >
                <div className="flex items-center gap-3">
                  <SparklesIcon className="w-6 h-6 text-purple-600" />
                  <div>
                    <p className="font-medium text-purple-900">AI 智能出题</p>
                    <p className="text-sm text-purple-600">让 AI 根据科目和知识点自动生成题目</p>
                  </div>
                </div>
              </button>
              <button
                onClick={() => toast('手动创建考试功能即将上线', { icon: 'ℹ️' })}
                className="w-full p-4 border border-gray-200 rounded-xl text-left hover:bg-gray-50 transition"
              >
                <div className="flex items-center gap-3">
                  <DocumentTextIcon className="w-6 h-6 text-gray-600" />
                  <div>
                    <p className="font-medium text-gray-900">手动创建</p>
                    <p className="text-sm text-gray-500">从题库中选择题目组卷</p>
                  </div>
                </div>
              </button>
            </div>
            <div className="flex justify-end">
              <button
                onClick={() => setShowCreateModal(false)}
                className="w-full sm:w-auto px-6 py-3 sm:py-2 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition"
              >
                关闭
              </button>
            </div>
          </div>
        </div>
      )}
    </StudentPageLayout>
  )
}

export default ExamCenter 